<template>
  <v-form ref="ADRESSE" lazy-validation @submit.prevent="save">
    <div>
      <section-header
          v-if="!hideTitle"
          ref="VALUATE_META_ATTRIB"
          :ressource="ressource"
          :is-extended="extended"
          :dialog="dialog"
          @extended=setExtended
          @close-dialog="closeDialog">
      </section-header>

      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <div v-for="(metaAttrib, index) in attribs" :key="index">
          <div v-if="metaAttrib.displayed">
            <edit-field
                v-if="metaAttrib.type == fieldTypes.STRING || metaAttrib.type == fieldTypes.INT || metaAttrib.type == fieldTypes.DECIMAL"
                :ref="'META_VALUE'+index"
                :value="metaAttrib.value"
                :disabled="disabled"
                :edit-field-info="fieldInfoMap.get(metaAttrib.id)"
                :context-id="metaAttrib.id"
                @updated="(value) => editFieldUpdated(value, metaAttrib)">
            </edit-field>

            <date-field
                v-if="metaAttrib.type == fieldTypes.DATE"
                :ref="'META_VALUE'+index"
                :value="metaAttrib.value"
                :disabled="disabled"
                :edit-field-info="fieldInfoMap.get(metaAttrib.id)"
                :context-id="metaAttrib.id"
                @updated="(value) => editFieldUpdated(value, metaAttrib)">
            </date-field>

            <edit-currency
                v-if="metaAttrib.type === fieldTypes.CURRENCY"
                :ref="'META_VALUE'+index"
                :value="metaAttrib.value"
                :disabled="disabled"
                :libelle="metaAttrib.text"
                :context-id="metaAttrib.id"
                @input="(value) => editFieldUpdated(value, metaAttrib)">
            </edit-currency>
          </div>
        </div>
      </div>

      <div v-if="extended & showButton" class="d-flex flex-row-reverse mt-4">
        <loader-button
            class='ms-2'
            type='submit'
            :label='applyButton'
            :disabled='!canSave'>
        </loader-button>
      </div>
    </div>
  </v-form>
</template>

<script lang="ts">
import MetaAttrib, {MetaAttribTypes} from '@/generated/classes/MetaAttrib';
import {EditFieldType, FieldInfo} from '@/framework/base/components/FieldInfo';
import EditField from '@/framework/base/components/EditField.vue';
import EditCurrency from '@/framework/base/components/EditCurrency.vue';
import DateField from '@/framework/base/components/DateField.vue';
import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import SectionHeader from '@/framework/base/components/SectionHeader.vue';
import {FwkButtons, FwkSections, RessourceFwkButton, RessourceFwkSection} from '@/ressources/globalRessources';
import {MapRessource} from '@/framework/base/helpers/Ressources';
import {Extended} from '@/framework/base/classes/Schema';
import {defineComponent, type PropType} from "vue";

export default defineComponent({
  components: {
    LoaderButton,
    SectionHeader,
    EditField,
    EditCurrency,
    DateField
  },
  props: {
    hideTitle: {
      type: Boolean
    },
    extended: {
      type: Boolean
    },
    dialog: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    showButton: {
      type: Boolean
    },
    metaAttribs: {
      type: Array as PropType<MetaAttrib[]>,
      required: true
    }
  },
  data() {
    const lastMetaAttribs: MetaAttrib[] = [];
    const cloneMetaAttribs: MetaAttrib[] = [];
    const fieldInfoMap: Map<number, FieldInfo> = new Map();

    return {
      canSave: false,
      editing: false,
      fieldInfoMap,
      cloneMetaAttribs,
      lastMetaAttribs,
      filterDialog: false,
      applyButton: RessourceFwkButton(FwkButtons.APPLIQUER).label
    };
  },
  computed: {
    ressource() {
      if (this.dialog) {
        return RessourceFwkSection(FwkSections.META_ATTRIBUT_DIALOG);
      } else {
        return RessourceFwkSection(FwkSections.META_ATTRIBUT);
      }
    },
    attribs(): MetaAttrib[] {
      if (this.cloneMetaAttribs.length == 0) {
        this.cloneMetaAttribs = JSON.parse(JSON.stringify(this.metaAttribs));
      }
      const needsRefresh = this.metaAttribs.some(attrib => {
        const clone = this.cloneMetaAttribs.find(c => c.id === attrib.id);
        return clone && clone.value !== attrib.value;
      });

      if (needsRefresh) {
        this.cloneMetaAttribs = JSON.parse(JSON.stringify(this.metaAttribs));
      }

      if (JSON.stringify(this.lastMetaAttribs) != JSON.stringify(this.cloneMetaAttribs) && !this.editing) {
        this.setLastMetaAttribs(this.cloneMetaAttribs);
        this.cloneMetaAttribs.forEach((metaAttrib: MetaAttrib) => {
          // metaAttrib.displayed = metaAttrib.value == '' || !this.dialog;
          metaAttrib.displayed = true;
          this.fieldInfoMap.set(metaAttrib.id, this.fieldInfo(metaAttrib));
        });
      }
      return this.cloneMetaAttribs;
    },
    fieldTypes() {
      return MetaAttribTypes;
    }
  },
  created() {
    this.metaAttribs.forEach((metaAttrib: MetaAttrib) => {
      //metaAttrib.displayed = !this.dialog || metaAttrib.value == '';
      metaAttrib.displayed = true;
    });
  },
  methods: {
    setLastMetaAttribs(value: MetaAttrib[]) {
      this.lastMetaAttribs = [];
      value.forEach((metaAttrib: MetaAttrib) => {
        this.lastMetaAttribs.push(MetaAttrib.fromJson(metaAttrib));
      });
    },
    fieldInfo(metaAttrib: MetaAttrib): FieldInfo {
      const ressource = new MapRessource();
      ressource.label = metaAttrib.text;
      const type = this.fieldInfoType(metaAttrib);
      const precision = 2;
      const format = null;
      const mask = null;
      const live = null;
      const focus = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live);
    },
    fieldInfoType(metaAttrib: MetaAttrib): EditFieldType {
      if (metaAttrib.type == MetaAttribTypes.STRING) {
        return EditFieldType.STRING;
      } else if (metaAttrib.type == MetaAttribTypes.INT) {
        return EditFieldType.INT;
      } else if (metaAttrib.type == MetaAttribTypes.DECIMAL) {
        return EditFieldType.DECIMAL;
      } else if (metaAttrib.type == MetaAttribTypes.DATE) {
        return EditFieldType.DATE;
      } else if (metaAttrib.type == MetaAttribTypes.CURRENCY) {
        return EditFieldType.CURRENCY;
      } else {
        return EditFieldType.NONE;
      }
    },
    editFieldUpdated(value: string, metaAttrib: MetaAttrib) {
      this.editing = true;
      metaAttrib.value = value;
      this.setUpdatedAndValid(metaAttrib);
    },
    setUpdatedAndValid(metaAttrib: MetaAttrib): void {
      const clone = this.metaAttribs.find(a => metaAttrib.id == a.id);
      let updated = false;
      if (clone && clone != metaAttrib) {
        clone.value = metaAttrib.value;
        updated = true;
      }
      const error = this.checkFields();
      this.canSave = !error && updated && !this.disabled;
      if (!this.showButton) {
        this.editing = false;
        this.$emit('metaAttribUpdated', this.canSave);
      }
    },
    checkFields(): boolean {
      let error = false;
      this.cloneMetaAttribs.forEach((metaAttrib, index) => {
        if (!error) {
          // @ts-expect-error : generated method
          if (this.$refs['META_VALUE' + index][0]) {
            // @ts-expect-error : generated method
            if (this.$refs['META_VALUE' + index][0].touch()) {
              error = true;
            }
          }
        }
      })
      return error;
    },
    save() {
      if (!this.checkFields()) {
        MetaAttrib.valuate(this.cloneMetaAttribs).then(() => {
          this.$emit('closeDialog');
          this.canSave = false;
          this.editing = false;
        });
      }
    },
    setExtended(extended: Extended): void {
      this.$emit('extended', extended);
    },
    closeDialog() {
      this.$emit('closeDialog');
    }
  }
})

</script>
